import { useState, createContext, useEffect, useContext } from 'react';
import { useUserUpdaterProvider } from '../user_context/UserUpdaterProvider';
import { EmergencyMessage } from '../../models/Emergency/EmergencyMessage';
import { UserHistoryObservedEmergencyModel } from '@/models/Emergency/UserHistoryObservedEmergencyModel';
import { Emergency } from '@/models/Emergency/Emergency';
/**
 * @typedef {Object} EmergencyUpdaterContextType
 * @property {Record<string, UserHistoryObservedEmergencyModel>} observedEmergencies - Emergencies observed by the user.
 * @property {Record<string, EmergencyMessage[]>} activeEmergencyMessages - Active emergency messages.
 * @property {(incomingEmergency: Emergency) => void} updateEmergencies - Function to update or add an emergency.
 * @property {(incomingMessage: EmergencyMessage) => void} updateEmergencyMessages - Function to update or add an emergency message.
 */

export const EmergencyUpdaterContext = createContext();

export const EmergencyUpdaterProvider = ({ children }) => {
  const { userHistory } = useUserUpdaterProvider();

  /** @type {[Record<string, UserHistoryObservedEmergencyModel>, React.Dispatch<React.SetStateAction<Record<string, UserHistoryObservedEmergencyModel>>>]} */
  const [observedEmergencies, setObservedEmergencies] = useState({});
  /** @type {[Record<string, EmergencyMessage[]>, React.Dispatch<React.SetStateAction<Record<string, EmergencyMessage[]>>>]} */
  const [activeEmergencyMessages, setActiveEmergencyMessages] = useState({});

  useEffect(() => {
    if (userHistory?.observed_emergencies) {
      /**
       * @type {Object.<string, UserHistoryObservedEmergencyModel>}
       */
      const temporaryObservedEmergencies = {};
      Object.entries(userHistory.observed_emergencies).forEach(
        ([key, emergencyData]) => {
          const emergency = emergencyData;
          temporaryObservedEmergencies[key] = emergency;
        },
      );
      setObservedEmergencies(temporaryObservedEmergencies);
    }
  }, [userHistory]);

  /**
   *
   * @param {EmergencyMessage} incomingMessage
   */
  const updateEmergencyMessages = (incomingMessage) => {
    const newMessage =
      incomingMessage instanceof EmergencyMessage
        ? incomingMessage
        : new EmergencyMessage(incomingMessage);

    setActiveEmergencyMessages((prevMessages) => {
      const { emergency_id, id } = incomingMessage;
      const updatedMessages = { ...prevMessages };
      if (updatedMessages[emergency_id]) {
        const existingMessages = updatedMessages[emergency_id];
        const updatedDataIndex = existingMessages.findIndex(
          (message) => message.id === id,
        );
        if (updatedDataIndex >= 0) {
          existingMessages[updatedDataIndex] = newMessage;
        } else {
          existingMessages.push(newMessage);
        }
      } else {
        updatedMessages[emergency_id] = [newMessage];
      }
      return updatedMessages;
    });
  };

  /**
   * Updates the observed emergencies state with a new or updated emergency.
   * @param {Emergency} incomingEmergency - The emergency object or data to create it.
   * @returns {void}
   */
  const updateEmergencies = (incomingEmergency) => {
    setObservedEmergencies((prevObservedEmergencies) => {
      const { emergency_id } = incomingEmergency;
      const updatedEmergencies = { ...prevObservedEmergencies };
      const updatedEmergency =
        incomingEmergency instanceof UserHistoryObservedEmergencyModel
          ? incomingEmergency
          : new UserHistoryObservedEmergencyModel(incomingEmergency);
      updatedEmergencies[emergency_id] = updatedEmergency;
      return updatedEmergencies;
    });
  };

  return (
    <EmergencyUpdaterContext.Provider
      value={{
        observedEmergencies,
        activeEmergencyMessages,
        updateEmergencies,
        updateEmergencyMessages,
      }}
    >
      {children}
    </EmergencyUpdaterContext.Provider>
  );
};

/**
 * Custom hook to access the EmergencyUpdater context.
 * @returns {EmergencyUpdaterContextType}
 */
export const useEmergencyUpdaterProvider = () =>
  useContext(EmergencyUpdaterContext);
